import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, FileCheck2, Sparkles, Home, Briefcase, Users2, Tag,
  Shield, MailCheck, Crown, ArrowRight, Check, Star, MapPin, HeartPulse,
  Globe2, Languages, BookOpen,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";

const FEATURES = [
  { icon: FileCheck2, title: "Démarches administratives", desc: "CAF, CROUS, Ameli, Carte d'identité, sécu, impôts, banque… 15 démarches guidées pas à pas." },
  { icon: Home, title: "Logement étudiant", desc: "CROUS, CAF (APL/ALS), Action Logement, Visale, garants. Trouve, candidate, signe." },
  { icon: Briefcase, title: "Jobs & alternance", desc: "CDD étudiants, alternance, stages, baby-sitting. Plateformes filtrées + calculatrice salaire net." },
  { icon: Sparkles, title: "Assistant IA dédié", desc: "CV étudiant, lettre de motivation, prépa entretien — propulsés par Claude Sonnet 4.5." },
  { icon: Tag, title: "Bons plans exclusifs", desc: "SNCF Avantage, cinéma, sport, culture, festivals. Toutes les réductions étudiantes en un endroit." },
  { icon: Users2, title: "Communauté locale", desc: "Forum par ville : coloc, sous-loc, conseils, entraide entre étudiants de ta fac." },
  { icon: HeartPulse, title: "Bien-être", desc: "Soutien psy gratuit (Santé Psy Étudiant), CROUS, urgences, addictions — toutes les ressources." },
  { icon: Shield, title: "Sécurité & RGPD", desc: "2FA, chiffrement, vérification email, droit à l'oubli. Tes données restent en France." },
];

const TESTIMONIALS = [
  { name: "Léa, 21 ans", role: "Étudiante en droit · Lyon", quote: "J'ai fait tous mes papiers de rentrée en une après-midi. La CAF et le CROUS d'un coup, sans paniquer.", stars: 5 },
  { name: "Yanis, 23 ans", role: "Master info · Paris", quote: "Le générateur de CV est bluffant. J'ai eu 3 entretiens en 2 semaines avec une lettre faite en 30 secondes.", stars: 5 },
  { name: "Sarah, 19 ans", role: "BUT GEA · Bordeaux", quote: "La communauté m'a aidée à trouver une coloc. Les bons plans SNCF, je ne les avais jamais vus ailleurs.", stars: 5 },
];

const FAQ = [
  {
    q: "Est-ce que Student Hub est vraiment gratuit ?",
    a: "Oui. Toutes les démarches administratives, la communauté, le suivi de progression et le générateur de CV (jusqu'à un certain quota) sont 100 % gratuits. Premium (2,99 €/mois) débloque l'IA illimitée, les modèles juridiques avancés et les bons plans en avant-première.",
  },
  {
    q: "Mes données sont-elles en sécurité ?",
    a: "Oui. Authentification avec mot de passe haché (bcrypt), vérification email obligatoire, 2FA optionnelle, chiffrement HTTPS bout-en-bout, hébergement en France conforme RGPD. Tu peux exporter ou supprimer toutes tes données à tout moment depuis tes paramètres.",
  },
  {
    q: "À qui s'adresse Student Hub ?",
    a: "À tous les étudiants en France : lycéens en transition, BUT, BTS, prépa, université, master, doctorat, écoles d'ingé et de commerce. Que tu sois français ou étudiant international, l'app est faite pour toi.",
  },
  {
    q: "Comment fonctionne l'assistant IA ?",
    a: "L'IA est alimentée par Claude Sonnet 4.5 (Anthropic), un des modèles les plus avancés au monde. Tu lui décris ton parcours et l'offre visée, elle génère un CV ou une lettre adaptés à la culture française (Europass-compatible).",
  },
  {
    q: "Puis-je résilier mon abonnement Premium quand je veux ?",
    a: "Oui. Tu peux annuler en un clic depuis tes paramètres (portail Stripe). Aucun engagement, aucun frais caché. Tu gardes l'accès jusqu'à la fin de la période payée.",
  },
  {
    q: "Y a-t-il une appli mobile ?",
    a: "Pas encore. Student Hub est actuellement une web app responsive (s'affiche parfaitement sur mobile). Une PWA installable est dans la roadmap pour début 2026.",
  },
];

export default function LandingEtudiants() {
  const canonical = "https://studenthubfrance.fr/landing/etudiants";
  const title = "Student Hub — Démarches, logement, jobs et IA pour étudiants en France (Erasmus & internationaux)";
  const description = "L'application tout-en-un pour réussir ta vie étudiante en France. CAF, CROUS, Ameli, logement, jobs, CV/lettre IA. Communauté traduisible en 8 langues pour étudiants Erasmus et internationaux. Gratuit, sécurisé, made in France.";
  const ogImage = "https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=1200&q=85";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Student Hub",
    url: "https://studenthubfrance.fr",
    description,
    applicationCategory: "EducationApplication",
    operatingSystem: "Web",
    inLanguage: "fr-FR",
    offers: [
      { "@type": "Offer", name: "Gratuit", price: "0", priceCurrency: "EUR" },
      { "@type": "Offer", name: "Premium mensuel", price: "2.99", priceCurrency: "EUR" },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      ratingCount: "127",
      bestRating: "5",
    },
    publisher: {
      "@type": "Organization",
      name: "Student Hub",
      url: "https://studenthubfrance.fr",
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  useSeo({
    title,
    description,
    canonical,
    ogImage,
    jsonLd,
    lang: "fr",
    hreflangs: [
      { lang: "fr", url: "https://studenthubfrance.fr/landing/etudiants" },
      { lang: "en", url: "https://studenthubfrance.fr/en/students" },
      { lang: "x-default", url: "https://studenthubfrance.fr/landing/etudiants" },
    ],
  });

  // Inject FAQ JSON-LD separately (second script block)
  React.useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(faqLd);
    el.dataset.seoHook = "faq";
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white" data-testid="landing-etudiants">
      {/* Subtle bg accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-violet-100 blur-3xl opacity-60" />
        <div className="absolute top-1/3 -right-40 w-[34rem] h-[34rem] rounded-full bg-blue-100 blur-3xl opacity-50" />
      </div>

      {/* Top bar */}
      <header className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-500/30">
            <GraduationCap size={18} />
          </div>
          <span className="font-display text-lg font-bold text-slate-900">Student Hub</span>
        </Link>
        <nav className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-violet-600 transition-colors" data-testid="landing-login-link">
            Se connecter
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
            data-testid="landing-register-link"
          >
            Commencer <ArrowRight size={14} />
          </Link>
        </nav>
      </header>

      {/* HERO */}
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <section className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center pt-10 sm:pt-20">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 border border-violet-200 px-3 py-1 mb-5">
              <Sparkles size={14} className="text-violet-600" />
              <span className="text-xs font-semibold text-violet-700 tracking-wide">Powered by Claude Sonnet 4.5 · Made in France 🇫🇷</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]">
              Tout pour ta vie étudiante <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">en une seule appli</span>
            </h1>
            <p className="text-slate-600 mt-5 text-lg leading-relaxed max-w-xl">
              Démarches CAF, CROUS, Ameli. Logement, jobs, alternance. CV et lettres générés par IA. Bons plans exclusifs. Communauté étudiante par ville. <strong>Gratuit, sécurisé, sans pub.</strong>
            </p>
            {/* Multilingue badge — rassure les étudiants internationaux / Erasmus */}
            <div
              className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 text-xs font-medium text-emerald-800 shadow-sm"
              data-testid="hero-multilingue-badge"
              title="Chat communautaire et annonces traduisibles à la volée dans 8 langues"
            >
              <span aria-hidden="true">🌐</span>
              <span className="font-semibold">Multilingue</span>
              <span className="text-emerald-700/80">· FR · EN · ES · AR · 中文 · DE · IT · PT</span>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-base font-semibold shadow-xl shadow-violet-600/30 transition-all hover:shadow-2xl"
                data-testid="hero-primary-cta"
              >
                Créer mon compte gratuit <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 hover:border-slate-400 text-slate-900 text-base font-medium bg-white transition-colors"
                data-testid="hero-secondary-cta"
              >
                J'ai déjà un compte
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> 100 % gratuit</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> RGPD · Données en France</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> Sans pub, sans tracker</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> Compte supprimable en 1 clic</span>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=900&q=85"
              alt="Étudiants célébrant leur remise de diplôme — Student Hub, l'application étudiante française"
              className="w-full max-w-md mx-auto rounded-3xl shadow-2xl object-cover aspect-[4/5]"
              loading="eager"
            />
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-2xl shadow-xl px-4 py-3 max-w-xs">
              <div className="flex items-center gap-2 mb-1">
                {[1,2,3,4,5].map((s) => <Star key={s} size={14} className="fill-amber-400 text-amber-400" />)}
                <span className="text-xs font-semibold text-slate-700">4,9 / 5</span>
              </div>
              <p className="text-xs text-slate-600">« Tout est centralisé. Plus besoin de courir entre 8 sites. »</p>
            </div>
          </div>
        </section>

        {/* FEATURES grid */}
        <section className="mt-24" id="features">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Ce que tu trouves dans l'app</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">8 piliers pour réussir ta vie étudiante</h2>
            <p className="text-slate-600 mt-3">Pensé pour les étudiants français et internationaux. Conforme RGPD. Hébergé en France.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-lg transition-all">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/30">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display font-semibold text-slate-900 text-base mb-1.5">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* INTERNATIONAL STUDENTS — Erasmus & non-francophones */}
        <section className="mt-24" id="international" aria-labelledby="international-title">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-50 via-blue-50 to-violet-50 border border-emerald-100 p-8 sm:p-12">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-xs font-semibold text-emerald-800 shadow-sm">
                  <Globe2 size={14} className="text-emerald-600" />
                  Erasmus · Étudiants internationaux
                </div>
                <h2 id="international-title" className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-4 leading-tight">
                  Tu arrives en France pour tes études ? Student Hub parle ta langue.
                </h2>
                <p className="text-slate-700 mt-4 leading-relaxed">
                  Que tu sois en programme Erasmus, en mobilité internationale, en stage de fin d'études ou en master en France pour la première fois, l'administration française peut être intimidante. Student Hub centralise <strong>toutes</strong> tes démarches (CAF, CROUS, Préfecture, Sécurité sociale) avec des guides en français clair, et te connecte à une communauté d'étudiants bienveillante — traduisible à la volée dans <strong>8 langues</strong>.
                </p>
                <ul className="mt-6 space-y-3" data-testid="international-bullets">
                  <li className="flex items-start gap-3" data-testid="intl-bullet-translate">
                    <div className="w-9 h-9 rounded-xl bg-white border border-emerald-200 flex items-center justify-center shrink-0 shadow-sm">
                      <Languages size={16} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Traduis tous les messages communautaires en un clic</p>
                      <p className="text-sm text-slate-600 mt-0.5">Chat anonyme et forum d'annonces traduits à la demande : FR · EN · ES · AR · 中文 · DE · IT · PT. Aucun message ne te restera incompréhensible.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="intl-bullet-charter">
                    <div className="w-9 h-9 rounded-xl bg-white border border-blue-200 flex items-center justify-center shrink-0 shadow-sm">
                      <Users2 size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Charte de bienveillance Erasmus-friendly</p>
                      <p className="text-sm text-slate-600 mt-0.5">Zéro jugement, zéro discrimination. Modération anti-harcèlement (3 signalements = message auto-masqué) et urgences psy gratuites (3114, Nightline). Tu es accueilli·e comme chez toi.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="intl-bullet-guides">
                    <div className="w-9 h-9 rounded-xl bg-white border border-violet-200 flex items-center justify-center shrink-0 shadow-sm">
                      <BookOpen size={16} className="text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Démarches expliquées pour les non-francophones</p>
                      <p className="text-sm text-slate-600 mt-0.5">CAF, CROUS, Préfecture (titre de séjour étudiant), Ameli, ouverture de compte bancaire, Visale. Chaque étape est simplifiée, avec les vrais liens officiels.</p>
                    </div>
                  </li>
                </ul>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors shadow-md"
                    data-testid="intl-cta-register"
                  >
                    Démarrer en français facile <ArrowRight size={14} />
                  </Link>
                  <span className="text-xs text-slate-500">Gratuit · Aucune carte bancaire requise</span>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative rounded-2xl bg-white border border-slate-200 shadow-xl p-5 max-w-sm mx-auto">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold">A</div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-900 truncate">Ahmed · Erasmus Maroc</p>
                      <p className="text-[10px] text-slate-500">Master 1 · Lyon · il y a 2 min</p>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      🇬🇧 Traduit
                    </span>
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed">
                    Hi! I just arrived in Lyon for Erasmus and I'm looking for tips on getting my CAF housing aid quickly. Anyone has been through this recently?
                  </p>
                  <p className="text-xs text-slate-400 italic mt-2">→ Original arabe traduit automatiquement</p>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">❤️ 12</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">🤗 5</span>
                    <span className="text-slate-400 ml-auto">Communauté Student Hub</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="mt-24" id="temoignages">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Ils nous font confiance</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Des étudiants qui ont gagné du temps</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="rounded-2xl border border-slate-200 bg-white p-6 relative">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="text-slate-700 text-sm leading-relaxed">« {t.quote} »</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-slate-100">
                  <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={11} /> {t.role}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section className="mt-24" id="pricing">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Tarifs simples</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Gratuit pour toujours. Premium si tu veux plus.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="pricing-grid">
            <div className="rounded-2xl border-2 border-slate-200 bg-white p-7">
              <p className="text-sm font-semibold text-slate-700">Gratuit</p>
              <p className="font-display text-4xl font-bold text-slate-900 mt-2">0 €<span className="text-base font-normal text-slate-500">/mois</span></p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> Toutes les démarches guidées</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> Communauté locale par ville</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> Stockage de documents sécurisé</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> IA : 5 générations / mois</li>
              </ul>
              <Link to="/register" className="mt-6 block text-center px-5 py-3 rounded-full border border-slate-300 hover:border-slate-400 font-semibold text-sm">
                Commencer gratuitement
              </Link>
            </div>
            <div className="rounded-2xl border-2 border-violet-400 bg-gradient-to-br from-white via-violet-50/30 to-blue-50/30 p-7 relative">
              <span className="absolute -top-3 right-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-[10px] font-bold tracking-wide uppercase">
                <Crown size={11} /> Recommandé
              </span>
              <p className="text-sm font-semibold text-violet-700">Premium</p>
              <p className="font-display text-4xl font-bold text-slate-900 mt-2">2,99 €<span className="text-base font-normal text-slate-500">/mois</span></p>
              <p className="text-xs text-emerald-700 mt-1">7 jours gratuits · Sans engagement</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Tout du plan Gratuit</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> IA illimitée (CV, lettres, entretien)</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Modèles juridiques (bail, recours…)</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Bons plans en avant-première</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Zéro pub</li>
              </ul>
              <Link to="/register" className="mt-6 block text-center px-5 py-3 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-sm shadow-lg shadow-violet-600/30 hover:shadow-xl transition-shadow">
                Essayer 7 jours gratuits
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24" id="faq">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Questions fréquentes</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Tout ce que tu veux savoir</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-2" data-testid="faq-list">
            {FAQ.map((item, idx) => (
              <details key={idx} className="group rounded-xl border border-slate-200 bg-white px-5 py-4 hover:border-violet-300 transition-colors">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-semibold text-slate-900 text-sm sm:text-base pr-4">{item.q}</span>
                  <span className="shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-lg group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mt-24 rounded-3xl bg-gradient-to-br from-blue-600 via-violet-600 to-fuchsia-600 p-10 sm:p-14 text-center text-white overflow-hidden relative" data-testid="final-cta">
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
              Prêt à simplifier ta vie étudiante ?
            </h2>
            <p className="mt-3 text-violet-100 text-base">
              Rejoins gratuitement Student Hub et débloque toutes les démarches, l'IA et la communauté. Aucune carte bancaire requise.
            </p>
            <Link
              to="/register"
              className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-slate-900 text-base font-bold shadow-2xl hover:bg-slate-100 transition-colors"
              data-testid="final-cta-btn"
            >
              Créer mon compte gratuit <ArrowRight size={16} />
            </Link>
            <p className="mt-4 text-xs text-violet-200 flex items-center justify-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><MailCheck size={12} /> Vérification email</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Shield size={12} /> 2FA disponible</span>
              <span>·</span>
              <span>Sans engagement · Annulation 1 clic</span>
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center">
                <GraduationCap size={14} />
              </div>
              <span className="font-display font-bold text-slate-900">Student Hub</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">L'appli tout-en-un pour étudiants en France. Made in France 🇫🇷.</p>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-2">Produit</p>
            <ul className="space-y-1.5 text-slate-600">
              <li><Link to="/register" className="hover:text-violet-600">Créer un compte</Link></li>
              <li><Link to="/login" className="hover:text-violet-600">Se connecter</Link></li>
              <li><Link to="/blog" className="hover:text-violet-600">Blog & guides</Link></li>
              <li><a href="#features" className="hover:text-violet-600">Fonctionnalités</a></li>
              <li><a href="#pricing" className="hover:text-violet-600">Tarifs</a></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-2">Légal</p>
            <ul className="space-y-1.5 text-slate-600">
              <li><Link to="/legal/cgu" className="hover:text-violet-600">CGU</Link></li>
              <li><Link to="/legal/confidentialite" className="hover:text-violet-600">Confidentialité</Link></li>
              <li><Link to="/legal/mentions" className="hover:text-violet-600">Mentions légales</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-violet-600">Cookies</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-2">Contact</p>
            <p className="text-xs text-slate-500">contact.servicedecomdu64@gmail.com</p>
            <p className="text-xs text-slate-500 mt-1">6 avenue de la Légion Tchèque, 64100 Bayonne</p>
          </div>
        </div>
        <div className="border-t border-slate-200 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Student Hub · SIRET 422 541 649 00022
        </div>
      </footer>
    </div>
  );
}
